import {
    Avatar, Box, Chip, CircularProgress, IconButton, InputAdornment,
    TextField, Tooltip, Typography
} from "@mui/material"
import {
    AccessTimeOutlined, HelpOutlineOutlined, PersonAddOutlined,
    Refresh, Search, SwapHorizOutlined
} from "@mui/icons-material"
import { FC, useCallback, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import PageTitle from "../Common/PageTitle"
import NoData from "../Common/NoData"
import MenuListPop from "../Common/menu"
import { EditOutlined, DeleteOutlineOutlined, PersonOutlined } from "@mui/icons-material"
import { Apis } from "../../helpers"
import { Connect, mapStateToProps } from "../../Redux"
import { PropertyProps } from "../../types/properties"
import { UserProps } from "../../types/user"
import _ from 'lodash'
import FindUserModal, { FoundUser } from "./findUserModal"
import RolesInfoModal from "./rolesInfoModal"
import RemoveCollaboratorDialog from "./removeConfirm"

interface CollaboratorRow {
    _id: string
    firstName: string
    lastName: string
    userName?: string
    profilePhotos?: any
    email?: string
    phone?: string
    role: string
    status: 'accepted' | 'pending'
}
interface Props {
    selectedProperty?: PropertyProps
    User?: UserProps
}
interface StateProps {
    collaborators: CollaboratorRow[]
    loading: boolean
    refreshing: boolean
    searchVal: string
    showFindUser: null | 'add' | 'transfer'
    showRolesInfo: boolean
    removing: CollaboratorRow | null
    removingBusy: boolean
}

const ROLE_COLORS: Record<string, string> = {
    Owner: '#4CAF50', Agent: '#2196F3', Caretaker: '#FF9800', 'Debt Collector': '#9C27B0', Viewer: '#607D8B'
}
const initials = (first = '', last = '') => `${first[0] || ''}${last[0] || ''}`.toUpperCase()

const Collaborators: FC<Props> = ({ selectedProperty, User }) => {
    const navigate = useNavigate()
    const [state, setState] = useState<StateProps>({
        collaborators: [],
        loading: true,
        refreshing: false,
        searchVal: '',
        showFindUser: null,
        showRolesInfo: false,
        removing: null,
        removingBusy: false
    })
    const { collaborators, loading, refreshing, searchVal, showFindUser, showRolesInfo, removing, removingBusy } = state

    const fetchCollaborators = ({ refreshing }: { refreshing?: boolean } = {}) => {
        if (!selectedProperty?._id) return
        setState(s => ({ ...s, loading: !refreshing, refreshing: Boolean(refreshing) }))
        Apis({ module: 'Properties', action: 'getPropertyCollaborators', propertyId: selectedProperty._id })
            .then((resp: any) => setState(s => ({ ...s, collaborators: resp?.data || [], loading: false, refreshing: false })))
            .catch(() => setState(s => ({ ...s, loading: false, refreshing: false })))
    }

    useEffect(() => {
        fetchCollaborators()
        // eslint-disable-next-line
    }, [selectedProperty?._id])

    const isOwner = Boolean(collaborators.find(c => c._id === User?._id && c.role === 'Owner'))

    const search = useCallback(_.debounce((val: string) => setState(s => ({ ...s, searchVal: val })), 300), [])
    const filtered = collaborators.filter(c => {
        if (!searchVal.trim()) return true
        const val = searchVal.trim().toLowerCase()
        return [c.firstName, c.lastName, c.userName, c.role].some(f => f?.toLowerCase().includes(val))
    })

    const goToAdd = (collaborator?: FoundUser) => navigate('/add_collaborator', { state: { property: selectedProperty, collaborator } })
    const goToEdit = (row: CollaboratorRow) => navigate('/add_collaborator', { state: { property: selectedProperty, collaborator: row } })
    const goToTransfer = (collaborator: FoundUser) => navigate('/add_collaborator', { state: { property: selectedProperty, collaborator, transfer: true } })

    const removeCollaborator = () => {
        if (!removing || !selectedProperty?._id) return
        setState(s => ({ ...s, removingBusy: true }))
        Apis({ module: 'Properties', action: 'removeCollaborator', collaboratorId: removing._id, propertyId: selectedProperty._id })
            .then(() => setState(s => ({ ...s, removing: null, removingBusy: false, collaborators: s.collaborators.filter(c => c._id !== removing._id) })))
            .catch(() => setState(s => ({ ...s, removing: null, removingBusy: false })))
    }

    return (
        <Box sx={{ pr: 5, height: '100%' }}>
            <PageTitle title="Collaborators" />

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, mt: 3, flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {collaborators.length > 0 && (
                        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                            {collaborators.length.toLocaleString()} total
                        </Typography>
                    )}
                    <Tooltip title="Collaborator roles">
                        <IconButton size="small" onClick={() => setState(s => ({ ...s, showRolesInfo: true }))}>
                            <HelpOutlineOutlined sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                        size="small"
                        placeholder="Search..."
                        variant="outlined"
                        onChange={e => search(e.target.value)}
                        sx={{ width: 220, '& .MuiOutlinedInput-root': { fontSize: 13, borderRadius: 2 } }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search sx={{ fontSize: 17, color: 'text.disabled' }} />
                                </InputAdornment>
                            )
                        }}
                    />
                    <Tooltip title="Refresh">
                        <IconButton size="small" onClick={() => fetchCollaborators({ refreshing: true })} disabled={refreshing}>
                            {refreshing ? <CircularProgress size={18} /> : <Refresh sx={{ fontSize: 20 }} />}
                        </IconButton>
                    </Tooltip>
                    {isOwner && (
                        <>
                            <Tooltip title="Transfer Property">
                                <IconButton size="small" onClick={() => setState(s => ({ ...s, showFindUser: 'transfer' }))}>
                                    <SwapHorizOutlined sx={{ fontSize: 20 }} />
                                </IconButton>
                            </Tooltip>
                            <Box
                                onClick={() => setState(s => ({ ...s, showFindUser: 'add' }))}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 0.6, px: 1.5, py: 0.7,
                                    borderRadius: 2, bgcolor: '#8c7c0c', color: '#fff', cursor: 'pointer',
                                    fontSize: 13, fontWeight: 600, '&:hover': { bgcolor: '#6e6109' }
                                }}
                            >
                                <PersonAddOutlined sx={{ fontSize: 17 }} />
                                Add Collaborator
                            </Box>
                        </>
                    )}
                </Box>
            </Box>

            {!filtered.length || loading ?
                <NoData message="No collaborators found." loading={loading} searchVal={searchVal} /> :
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                    {filtered.map(row => {
                        const isSelf = row._id === User?._id
                        const roleColor = ROLE_COLORS[row.role] || '#8c7c0c'
                        const menuList = [
                            { label: 'Edit Role', icon: <EditOutlined /> },
                            { label: row.status === 'pending' ? 'Revoke Invite' : 'Remove', icon: <DeleteOutlineOutlined /> },
                            ...(row.role !== 'Owner' && row.status === 'accepted'
                                ? [{ label: 'Make Owner', icon: <PersonOutlined /> }]
                                : [])
                        ]
                        const onSelectMenu = (option: string) => {
                            if (option === 'Edit Role') goToEdit(row)
                            else if (option === 'Remove' || option === 'Revoke Invite') setState(s => ({ ...s, removing: row }))
                            else if (option === 'Make Owner') goToTransfer(row)
                        }
                        return (
                            <Box key={row._id} sx={{
                                display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: '#fff', borderRadius: 3, p: 1.8,
                                boxShadow: '0 1px 4px rgba(0,0,0,.06), 0 4px 16px rgba(0,0,0,.04)'
                            }}>
                                <Avatar src={row.profilePhotos?.small?.url} sx={{ width: 36, height: 36, fontSize: 13, fontWeight: 700 }}>
                                    {initials(row.firstName, row.lastName)}
                                </Avatar>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                                        <Typography sx={{ fontSize: 13.5, fontWeight: 700 }} noWrap>
                                            {`${row.firstName || ''} ${row.lastName || ''}`.trim() || row.userName || '—'}
                                        </Typography>
                                        {isSelf && <Chip label="You" size="small" sx={{ height: 18, fontSize: 10 }} />}
                                    </Box>
                                    <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }} noWrap>
                                        {row.phone || row.email || '—'}
                                    </Typography>
                                </Box>
                                {row.status === 'pending' && (
                                    <Chip
                                        icon={<AccessTimeOutlined sx={{ fontSize: 14 }} />}
                                        label="Pending"
                                        size="small"
                                        sx={{ fontSize: 11, color: '#d4890a', borderColor: '#d4890a', bgcolor: '#fdf2ec' }}
                                    />
                                )}
                                <Chip
                                    label={row.role}
                                    size="small"
                                    sx={{ fontSize: 11, color: roleColor, borderColor: roleColor, bgcolor: `${roleColor}14`, fontWeight: 600 }}
                                    variant="outlined"
                                />
                                {isOwner && !isSelf ? (
                                    <MenuListPop menuList={menuList} onSelect={onSelectMenu} />
                                ) : (
                                    <Box sx={{ width: 34 }} />
                                )}
                            </Box>
                        )
                    })}
                </Box>
            }

            {showFindUser && (
                <FindUserModal
                    propertyId={selectedProperty?._id}
                    isCollaborator
                    close={() => setState(s => ({ ...s, showFindUser: null }))}
                    onSelect={(user) => showFindUser === 'transfer' ? goToTransfer(user) : goToAdd(user)}
                />
            )}
            {showRolesInfo && <RolesInfoModal close={() => setState(s => ({ ...s, showRolesInfo: false }))} />}
            {removing && (
                <RemoveCollaboratorDialog
                    name={`${removing.firstName || ''} ${removing.lastName || ''}`.trim() || removing.userName || 'this collaborator'}
                    pending={removing.status === 'pending'}
                    processing={removingBusy}
                    onConfirm={removeCollaborator}
                    close={() => setState(s => ({ ...s, removing: null }))}
                />
            )}
        </Box>
    )
}

export default Connect(mapStateToProps)(Collaborators)
